import { viewTechDocRouteRef } from '@agilelab/plugin-wb-builder-catalog';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  AutoFixHighIcon,
  FormatListBulletedOffIcon,
  Severity,
  SeverityTooltip,
  SystemIcon,
  WbButton,
  WbDrawer,
  WbFavoriteEntityButton,
  WbFieldLabel,
  WbTagsArray,
} from '@agilelab/plugin-wb-platform';
import { DEFAULT_NAMESPACE, Entity } from '@backstage/catalog-model';
import { Link, MarkdownContent } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { catalogLocationCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { getEntitySourceLocation } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  TechDocsReaderPage,
  TechDocsReaderPageContent,
} from '@backstage/plugin-techdocs';
import { BackstageTheme } from '@backstage/theme';
import { JsonObject } from '@backstage/types';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  createStyles,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
  withStyles,
} from '@material-ui/core';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  CustomTemplate,
  findDuplicatesCustomTemplates,
} from '../ScaffolderPage/utils';

const EntityCardHeader = withStyles(theme => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.dark),
    position: 'relative',
    height: theme.spacing(8),
  },
  avatar: {
    backgroundColor: theme.palette.common.white,
    width: theme.spacing(7),
    height: theme.spacing(7),
    borderRadius: theme.spacing(2),
    color: theme.palette.primary.main,
    position: 'absolute',
    bottom: -8,
    left: 12,
    padding: theme.spacing(1),
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
  },
  title: {
    paddingLeft: theme.spacing(8),
    lineClamp: 2,
    boxOrient: 'vertical',
    overflow: 'hidden',
    display: 'box',
    fontSize: theme.typography.body1.fontSize,
    lineHeight: ' 1.125rem',
  },
}))(CardHeader);

const useErrorStyles = makeStyles<BackstageTheme>((theme: BackstageTheme) =>
  createStyles({
    chooseButton: {
      color: theme.palette.softened.error,
      borderColor: theme.palette.softened.error,
      '&:hover': {
        textDecoration: 'none',
      },
    },
    root: {
      backgroundColor: theme.palette.softened.error,
      color: theme.palette.secondary.dark,
    },
    cardActions: {
      backgroundColor: theme.palette.banner.error,
      padding: theme.spacing(1, 2),
      justifyContent: 'space-between',
    },
  }),
);

const useStyles = makeStyles<BackstageTheme>((theme: BackstageTheme) =>
  createStyles({
    cardSection: {
      marginBottom: '0.8em',
      overflowY: 'hidden',
      maxHeight: 100,
      '&:hover': {
        overflowY: 'scroll',
      },
    },
    box: {
      overflow: 'auto',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 10,
      '-webkit-box-orient': 'vertical',
      paddingBottom: '0.8em',
      paddingTop: '8px',
    },
    description: {
      '& > p': {
        margin: 0,
      },
    },
    label: {
      color: theme.palette.secondary.main,
      textTransform: 'uppercase',
      fontSize: '0.65rem',
      fontWeight: 'bold',
      letterSpacing: 0.5,
      lineHeight: 1,
      paddingBottom: '0.2rem',
    },
    leftButton: {
      marginRight: 'auto',
    },
    starButton: {
      padding: '0.25rem',
      color: theme.palette.white,
    },
    card: {
      borderRadius: theme.spacing(2),
      width: 'calc(20% - 12px)',
      [theme.breakpoints.down('md')]: {
        width: 'calc(25% - 8px)',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'calc(50% - 8px)',
      },
    },
    chooseButton: {
      '&:hover': {
        textDecoration: 'none',
      },
    },
    cardActions: {
      backgroundColor: theme.palette.bkg.primary,
      padding: theme.spacing(1, 2),
      justifyContent: 'space-between',
      flex: 1,
    },
    flexBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    avatar: {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '1.125rem',
      pointerEvents: 'none',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
    },
  }),
);

type EntityProps = {
  description: string;
  tags: string[];
  title: string;
  type: string;
  name: string;
};

const getEntityCardProps = (
  entity: Entity,
): EntityProps & { key: string; icon?: string; hasDoc?: boolean } => {
  const icon = (entity.metadata.mesh as JsonObject | undefined)?.icon as string;

  return {
    key: entity.metadata.uid!,
    name: entity.metadata.name,
    title: `${(entity.metadata.title || entity.metadata.name) ?? ''}`,
    type: (entity.spec?.type ?? '') as string,
    description: entity.metadata.description ?? '-',
    tags: (entity.metadata?.tags as string[]) ?? [],
    icon: icon,
  };
};

export const EntityCardSection = (props: {
  title?: string;
  children: ReactNode;
}) => {
  const { title, children } = props;
  const classes = useStyles();

  return (
    <Box className={classes.cardSection}>
      {title && <Typography className={classes.label}>{title}</Typography>}
      {children}
    </Box>
  );
};

export type EntityCardProps = {
  entity: Entity;
  error?: EntityErrorResult;
  href: string;
  children?: ReactNode;
  customTemplates?: CustomTemplate[];
} & CardProps;

export const EntityCard = ({
  entity,
  error,
  href,
  children,
  customTemplates,
  ...cardProps
}: EntityCardProps) => {
  const entityProps = getEntityCardProps(entity);
  const location = useLocation();
  const navigate = useNavigate();

  // These two variables are used when you come to this page from system page's "add component" button(and you already know some basic info)
  const knownSystem = location.state?.system;
  const knownDomain = location.state?.domain;

  const { allowed } = usePermission({
    permission: catalogLocationCreatePermission,
  });

  const classes = useStyles();
  const errorClasses = useErrorStyles();
  // TechDocs Link
  const viewTechDoc = useRouteRef(viewTechDocRouteRef);

  const viewTechDocsAnnotation =
    entity.metadata.annotations?.['backstage.io/techdocs-ref'];
  const entityRef = {
    namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
    kind: entity.kind,
    name: entity.metadata.name,
  };
  const viewTechDocsLink =
    !!viewTechDocsAnnotation && !!viewTechDoc && viewTechDoc(entityRef);

  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);
  const reverseProvisioningTemplateUrl = customTemplates?.find(
    customTemplate => customTemplate.kind === 'reverseprovisioningtemplate',
  );
  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );
  const [showDocs, setShowDocs] = useState<boolean>(false);
  const theme = useTheme();

  const icon = () => {
    if (entityProps.type === 'dataproduct') {
      return (
        <Box className={classes.avatar}>
          <SystemIcon style={{ width: '100%', height: '100%' }} />
        </Box>
      );
    }
    return (
      <Box
        className={classes.avatar}
        style={{
          backgroundImage: entityProps.icon ? `url(${entityProps.icon})` : '',
        }}
      >
        {!entityProps.icon &&
          entityProps.title
            .split(' ')
            .slice(0, 2)
            .map(s => s.charAt(0))}
      </Box>
    );
  };

  const tags = entityProps.tags?.map(tag => ({ tagFQN: tag })) ?? [];

  return (
    <>
      <Card {...cardProps}>
        <EntityCardHeader
          className={error?.errorMessage ? errorClasses.root : ''}
          title={
            <Typography style={{ paddingLeft: '64px' }} variant="h6">
              {entityProps.title}
            </Typography>
          }
          avatar={icon()}
          action={
            <WbFavoriteEntityButton
              entity={entity}
              style={{
                color: theme.palette.white,
                marginTop: theme.spacing(1),
                marginRight: theme.spacing(1),
              }}
            />
          }
        />
        <CardContent
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            height: '200px',
            minHeight: '200px',
            maxHeight: '200px',
            overflowX: 'hidden',
          }}
        >
          <Box className={classes.box}>
            <MarkdownContent
              content={entityProps.description}
              className={classes.description}
            />
          </Box>
          {children}
          <Box className={classes.flexBox}>
            <WbFieldLabel label="TAGS" />
            {!!tags.length ? (
              <WbTagsArray tags={tags} />
            ) : (
              <Typography>No tags</Typography>
            )}
          </Box>
        </CardContent>
        <CardActions
          className={
            error?.errorMessage ? errorClasses.cardActions : classes.cardActions
          }
        >
          <Box style={{ display: 'flex' }}>
            {sourceLocation && (
              <Tooltip title="Code">
                <IconButton
                  component={Link}
                  aria-label="Code"
                  disabled={!sourceLocation.locationTargetUrl}
                  to={sourceLocation.locationTargetUrl ?? '#'}
                  style={{ height: 'fit-content' }}
                  color="secondary"
                  className={classes.leftButton}
                  size="small"
                >
                  <ScmIntegrationIcon type={sourceLocation.integrationType} />
                </IconButton>
              </Tooltip>
            )}
            {reverseProvisioningTemplateUrl && (
              <Tooltip title="Reverse Provisioning Template registered. All entities created from this template can use reverse provisioning.">
                <IconButton
                  color="secondary"
                  className={classes.leftButton}
                  href={reverseProvisioningTemplateUrl.ref}
                  size="small"
                  aria-label="reverse provisioning"
                >
                  <AutoFixHighIcon />
                </IconButton>
              </Tooltip>
            )}
            {editTemplateUrl.result && (
              <Tooltip title="Edit Template registered. All entities created from this template can be edited using Editor Wizard.">
                <IconButton
                  color="secondary"
                  className={classes.leftButton}
                  href={editTemplateUrl.result.ref}
                  size="small"
                  aria-label="edit template"
                >
                  <FormatListBulleted />
                </IconButton>
              </Tooltip>
            )}
            {viewTechDocsLink && (
              <Tooltip title="Documentation">
                <IconButton
                  color="secondary"
                  className={classes.leftButton}
                  onClick={() => {
                    setShowDocs(true);
                  }}
                  size="small"
                  aria-label="documentation"
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            {error?.errorMessage && (
              <SeverityTooltip
                severity={Severity.Error}
                label={error.errorMessage}
              />
            )}
            {editTemplateUrl.error && (
              <SeverityTooltip
                severity={Severity.Error}
                label={editTemplateUrl.errorMessage ?? 'error'}
                icon={<FormatListBulletedOffIcon />}
                to={`/catalog/${
                  entity?.metadata.namespace ?? 'default'
                }/${entity?.kind.toLocaleLowerCase()}/${entity?.metadata.name}`}
              />
            )}
          </Box>
          {allowed && (
            <>
              <WbButton
                label="Choose"
                aria-label={`Choose ${entityProps.title}`}
                disabled={!!error?.errorMessage}
                onClick={() => {
                  const to =
                    knownDomain && knownSystem
                      ? `${href}?formData=%7B"domain"%3A"${knownDomain}"%2C"dataproduct"%3A"system:${knownSystem}"%7D`
                      : href;
                  navigate(to);
                }}
                color={error?.errorMessage ? undefined : 'primary'}
                size="small"
                variant="outlined"
                className={
                  error?.errorMessage
                    ? errorClasses.chooseButton
                    : classes.chooseButton
                }
              />
            </>
          )}
        </CardActions>
      </Card>
      <WbDrawer
        open={showDocs}
        setOpen={setShowDocs}
        title={`${entityProps.title} Documentation`}
        contentContainerStyle={{
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <TechDocsReaderPage
          onClose={() => setShowDocs(false)}
          entityRef={entityRef}
        >
          <TechDocsReaderPageContent />
        </TechDocsReaderPage>
      </WbDrawer>
    </>
  );
};
